import { useId } from 'react';
import { RefreshCw } from 'lucide-react';
import type { Asset } from './production';
import { speechVoices } from './modelKinds';
import { Field } from './shared';
import type { Schema } from '../api/client';

export type SpeechLine = {
  lineIndex: number;
  characterId: string;
  speaker: string;
  text: string;
  voice: string;
  language: string;
  startFrame: number;
  endFrame: number;
  sourceHash: string;
  asset?: Asset;
};

export function speechMatchesShot(lines: SpeechLine[], shot: Schema<'ProductionShot'> | undefined) {
  if (!shot) return false;
  if (!shot.dialogueLines) return lines.length === 1 && lines[0]?.text === shot.dialogue;
  return (
    lines.length === shot.dialogueLines.length &&
    lines.every((line) => {
      const current = shot.dialogueLines?.[line.lineIndex];
      return (
        !!current &&
        current.text === line.text &&
        current.characterId === line.characterId &&
        current.startFrame === line.startFrame &&
        current.endFrame === line.endFrame
      );
    })
  );
}

export function VoicePicker({
  value,
  change,
  disabled = false,
  label = '音色',
}: {
  value: string;
  change: (value: string) => void;
  disabled?: boolean;
  label?: string;
}) {
  const id = useId();
  return (
    <Field
      label={label}
      hint="可搜索官方音色或填写已有音色 ID。Eve / Rex 已通过中文实测，其他音色请先试听。"
    >
      <input
        list={id}
        value={value}
        onChange={(e) => change(e.target.value)}
        disabled={disabled}
        maxLength={100}
        placeholder="如 eve、rex"
        autoComplete="off"
      />
      <datalist id={id}>
        {speechVoices.map((voice) => (
          <option key={voice.id} value={voice.id}>
            {voice.label}
          </option>
        ))}
      </datalist>
    </Field>
  );
}

export function SpeechLines({
  lines,
  busy = false,
  regenerate,
  fps = 24,
}: {
  lines: SpeechLine[];
  busy?: boolean;
  regenerate?: ((index: number) => void) | undefined;
  fps?: number;
}) {
  return (
    <div className="speech-lines">
      {lines.map((line) => (
        <article key={line.lineIndex} className="speech-line">
          <div className="section-heading">
            <strong>
              第 {line.lineIndex + 1} 句 · {line.speaker || '旁白'}
            </strong>
            <small>
              {(line.startFrame / fps).toFixed(1)}–{(line.endFrame / fps).toFixed(1)} 秒 ·{' '}
              {line.voice}
            </small>
          </div>
          <p>{line.text}</p>
          {line.asset?.url ? (
            <audio
              controls
              preload="metadata"
              src={line.asset.url}
              aria-label={`${line.speaker || '旁白'}第 ${line.lineIndex + 1} 句`}
            />
          ) : (
            <p className="quiet">此句尚未取得音频</p>
          )}
          {line.asset &&
            line.asset.spec.durationSeconds > (line.endFrame - line.startFrame) / fps + 0.1 && (
              <p className="form-error">
                这句声音超出预留时间，请在制作页调整台词或时间后重新生成。
              </p>
            )}
          {regenerate && (
            <button
              className="button small secondary"
              disabled={busy}
              onClick={() => regenerate(line.lineIndex)}
            >
              <RefreshCw size={14} />
              重新生成这一句
            </button>
          )}
        </article>
      ))}
    </div>
  );
}
